import React, { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { FourStars, StarsDivider } from "../components/FourStars";
import { Star, Search, X } from "lucide-react";

export default function ActionsPage() {
  const [data, setData] = useState({ items: [], domains: [] });
  const [q, setQ] = useState("");
  const [domain, setDomain] = useState("");

  const load = useMemo(() => async () => {
    const params = new URLSearchParams();
    if (q) params.append("q", q);
    if (domain) params.append("domain", domain);
    const r = await api.get(`/actions/search?${params.toString()}`).catch(() => ({ data: { items: [], domains: [] } }));
    setData(r.data);
  }, [q, domain]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div data-testid="actions-page">
      <section className="relative py-24 md:py-32 bg-gradient-to-br from-[#0A0F1A] via-[#0052CC] to-[#0A0F1A] text-white overflow-hidden">
        <div className="absolute inset-0 stars-pattern opacity-15" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 text-center">
          <FourStars size={16} className="justify-center mb-5" />
          <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Bilan & Vision</div>
          <h1 className="font-display text-4xl md:text-6xl mt-3 max-w-3xl mx-auto leading-tight">
            Les actions qui transforment <span className="text-[#D4AF37]">la République Démocratique du Congo</span>
          </h1>
          <p className="text-white/75 mt-6 max-w-2xl mx-auto leading-relaxed">
            Découvrez les chantiers, programmes et engagements concrets portés par le Président pour le peuple congolais.
          </p>
        </div>
      </section>

      <section className="py-12 stars-pattern">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          {/* Search */}
          <div className="relative max-w-2xl mx-auto mb-6">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#475569]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher une action présidentielle…"
              data-testid="actions-search-input"
              className="w-full pl-11 pr-10 py-3 rounded-lg border border-black/10 bg-white text-sm focus:outline-none focus:border-[#D4AF37]"
            />
            {q && (
              <button onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#475569] hover:text-[#CE1126]">
                <X size={14} />
              </button>
            )}
          </div>

          {data.domains.length > 0 && (
            <div className="flex flex-wrap justify-center gap-2 mb-10" data-testid="actions-domains">
              <button
                onClick={() => setDomain("")}
                className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                  !domain ? "bg-[#D4AF37] text-[#0A0F1A] border-[#D4AF37] font-semibold" : "bg-white border-black/10 text-[#475569] hover:border-[#D4AF37]/40"
                }`}
              >
                Tous les domaines
              </button>
              {data.domains.map((d) => (
                <button
                  key={d.name}
                  onClick={() => setDomain(d.name)}
                  data-testid={`action-domain-${d.name}`}
                  className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                    domain === d.name ? "bg-[#D4AF37] text-[#0A0F1A] border-[#D4AF37] font-semibold" : "bg-white border-black/10 text-[#475569] hover:border-[#D4AF37]/40"
                  }`}
                >
                  {d.name}
                </button>
              ))}
            </div>
          )}

          {data.items.length === 0 ? (
            <div className="text-center py-20 text-[#475569] text-sm">
              Aucune action ne correspond à votre recherche.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {data.items.map((a, i) => (
                <motion.article
                  key={a.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className="bg-white rounded-2xl overflow-hidden border border-black/5 shadow-sm hover:shadow-xl transition-all flex flex-col md:flex-row"
                  data-testid={`action-detail-${i}`}
                >
                  <div className="md:w-2/5 overflow-hidden">
                    <img src={a.image_url} alt={a.title} className="w-full h-56 md:h-full object-cover" />
                  </div>
                  <div className="md:w-3/5 p-7 flex flex-col">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] font-semibold">{a.domain}</div>
                    <h3 className="font-display text-2xl text-[#0A0F1A] mt-2">{a.title}</h3>
                    <p className="text-sm text-[#475569] mt-3 leading-relaxed flex-1">{a.description}</p>
                    <div className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-[#0052CC] self-start">
                      <Star size={14} fill="#D4AF37" stroke="#D4AF37" /> {a.impact}
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>
      <StarsDivider />
    </div>
  );
}
